import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { I18nNamespace } from '@/lib/i18n-namespaces';
import { Button } from '@/components/ui/Button';
import { LicenseService } from '@/services/engine/license-service';

type ResultKind = 'success' | 'warning' | 'error';

const RESULT_CLASSES: Record<ResultKind, string> = {
    success: 'text-success-100',
    warning: 'text-warning-100',
    error: 'text-danger-200',
};

function serverDetail(error: unknown): string | null {
    if (typeof error === 'object' && error !== null) {
        const response = (
            error as { response?: { data?: { detail?: unknown } } }
        ).response;
        if (typeof response?.data?.detail === 'string') {
            return response.data.detail;
        }
    }
    return null;
}

export function LicenseUpload({ onApplied }: { onApplied: () => void }) {
    const { t } = useTranslation(I18nNamespace.SETTINGS);
    const inputRef = useRef<HTMLInputElement>(null);
    const [busy, setBusy] = useState(false);
    const [result, setResult] = useState<{
        kind: ResultKind;
        text: string;
    } | null>(null);

    const handleFile = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        event.target.value = '';
        if (!file) {
            return;
        }
        setBusy(true);
        setResult(null);
        try {
            const document: unknown = JSON.parse(await file.text());
            const applied = await LicenseService.apply(document);
            const warnings = applied.warnings
                .map((warning) =>
                    t('license.apply.overLimit', {
                        current: warning.current,
                        limit: warning.limit,
                        resource: t(`license.resources.${warning.resource}`, {
                            defaultValue: warning.resource,
                        }),
                    }),
                )
                .join(' ');
            const unverified = applied.allocationsChecked
                ? ''
                : t('license.apply.allocationsUnverified');
            setResult({
                kind: warnings || unverified ? 'warning' : 'success',
                text:
                    t('license.apply.success', {
                        status: t(`license.statusValues.${applied.status}`),
                    }) +
                    (warnings ? ` ${warnings}` : '') +
                    (unverified ? ` ${unverified}` : ''),
            });
            onApplied();
        } catch (error) {
            const text =
                error instanceof SyntaxError
                    ? t('license.apply.invalidFile')
                    : (serverDetail(error) ?? t('license.apply.error'));
            setResult({ kind: 'error', text });
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="flex flex-col gap-3">
            <span className="text-xs font-medium uppercase tracking-wide text-grey-300">
                {t('license.apply.section')}
            </span>
            <div className="border border-grey-600 rounded-[10px] p-4 flex flex-col gap-3">
                <span className="text-xs text-grey-300">
                    {t('license.apply.description')}
                </span>
                <input
                    ref={inputRef}
                    type="file"
                    accept="application/json,.json"
                    className="hidden"
                    onChange={handleFile}
                />
                <div>
                    <Button
                        variant="secondary"
                        size="medium"
                        loading={busy}
                        onClick={() => inputRef.current?.click()}
                    >
                        {t('license.apply.button')}
                    </Button>
                </div>
                {result && (
                    <span className={`text-xs ${RESULT_CLASSES[result.kind]}`}>
                        {result.text}
                    </span>
                )}
            </div>
        </div>
    );
}
